import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BFF_BASE_URL } from '../../auth/authApi';

export interface Prompt {
    id: string;
    key: string;
    label: string;
    content: string;
    version: number | null;
    created_at: string;
    updated_at: string;
    updated_by: string | null;
}

export interface PromptVersion {
    id: string;
    version: number;
    content: string;
    created_at: string;
    created_by: string | null;
    created_by_name: string | null;
    is_current: boolean;
    message_count: number;
}

export interface PromptsState {
    prompts: Prompt[];
    status: 'idle' | 'loading' | 'loaded' | 'failed';
    error: string | null;
    editingKey: string | null;
}

export const promptsInitialState: PromptsState = {
    prompts: [],
    status: 'idle',
    error: null,
    editingKey: null,
};

export const fetchAdminPrompts = createAsyncThunk(
    'prompts/fetchAdminPrompts',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BFF_BASE_URL}/admin/prompts`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch prompts'
            );
        }
    }
);

export const updateAdminPrompt = createAsyncThunk(
    'prompts/updateAdminPrompt',
    async (
        { key, content }: { key: string; content: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await axios.put(`${BFF_BASE_URL}/admin/prompts/${encodeURIComponent(key)}`, {
                content,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update prompt'
            );
        }
    }
);

const promptsSlice = createSlice({
    name: 'prompts',
    initialState: promptsInitialState,
    reducers: {
        setEditingKey: (state, action: PayloadAction<string | null>) => {
            state.editingKey = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminPrompts.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchAdminPrompts.fulfilled, (state, action) => {
                state.status = 'loaded';
                state.prompts = action.payload;
                state.error = null;
            })
            .addCase(fetchAdminPrompts.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            .addCase(updateAdminPrompt.pending, (state) => {
                state.error = null;
            })
            .addCase(updateAdminPrompt.fulfilled, (state, action) => {
                const updated = action.payload as Prompt;
                const index = state.prompts.findIndex(
                    (p) => p.key === updated.key
                );
                if (index !== -1) {
                    state.prompts[index] = updated;
                }
                state.editingKey = null;
            })
            .addCase(updateAdminPrompt.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const { setEditingKey } = promptsSlice.actions;

export default promptsSlice;
